// src/layouts/trafficmonitoring/monitoring/RouteView.js
import React, { useState, useEffect, useRef, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { CircularProgress, Grid } from "@mui/material";
import MDBox from "components/MDBox";

import TrafficList from "./TrafficList";
import TrafficBar from "./TrafficBar";
import TrafficMonitoringMap from "./trafficMonitoringMap/TrafficMonitoringMap";


import useRouteData from "../data/useRouteData";
import useTrafficMonitoringGeom from "../data/useTrafficMonitoringGeom";
import { fetchActiveEvents } from "../data/fetchActiveEvents";
import useNetworkData from "../data/useNetworkData";

const MemoTrafficList = React.memo(TrafficList);
const MemoTrafficMap = React.memo(TrafficMonitoringMap);

export default function RouteView() {
  const { t } = useTranslation();
  // 1️⃣ Poll routes & network geometry
  const { data: routes, loading: loadingRoutes } = useRouteData();
  const { data: netData, loading: loadingNetwork } = useTrafficMonitoringGeom();

  // 2️⃣ Local state & refs for “first load”
  const [selectedRoute, setSelectedRoute] = useState(null);
  const [networkSource, setNetworkSource] = useState(null);
  const [onlyActive, setOnlyActive] = useState([]);
  const [onlyJam, setOnlyJam] = useState([]);
  const [loadingEvents, setLoadingEvents] = useState(true);

  const hasLoadedNetwork = useRef(false);
  const hasLoadedEvents = useRef(false);

  // 3️⃣ Default‐select the very first route
 useEffect(() => {
   if (!Array.isArray(routes) || routes.length === 0) return;

   // If nothing selected yet, default to first:
   if (!selectedRoute) {
     setSelectedRoute(routes[0]);
     return;
   }

   // Try to find a matching route in the new array by ID:
   const fresh = routes.find(r => r.route_id === selectedRoute.route_id);
   if (fresh) {
     // Re-assign to the new object reference
     setSelectedRoute(fresh);
   } else {
     // Fallback if it really vanished
     setSelectedRoute(routes[0]);
   }
 }, [routes]);

  // 4️⃣ Build GeoJSON for the selected route
  useEffect(() => {
    if (selectedRoute && netData?.features) {
      // const filtered = netData.features
      //   .filter(f => f.properties.route_id === selectedRoute.route_id)
      //   .map(f => {
      //     const flatCoords = f.geometry.coordinates
      //       .reduce((allPoints, segment) => allPoints.concat(segment), []);
      //     return {
      //       ...f,
      //       geometry: {
      //         type: "LineString",
      //         coordinates: flatCoords,
      //       },
      //     }
      //   })
      // setNetworkSourceEvents({
      //   type: "FeatureCollection",
      //   features: filtered
      // });

      setNetworkSource({
        type: "FeatureCollection",
        features: netData.features
          .filter(f => f.properties.route_id === selectedRoute.route_id)
      });
    } else {
      setNetworkSource(null);
      // setNetworkSourceEvents(null)
    }
  }, [netData, selectedRoute]);

  // 5️⃣ Fetch & poll events every minute
  useEffect(() => {
    if (!networkSource?.features?.length) {
      setOnlyActive([]);
      setOnlyJam([]);
      setLoadingEvents(false);
      return;
    }
    let cancelled = false;
    const load = async () => {
      setLoadingEvents(true);
      try {


        const filtered = netData.features
          .filter(f => f.properties.route_id === selectedRoute.route_id)
          .map(f => {
            const flatCoords = f.geometry.coordinates
              .reduce((allPoints, segment) => allPoints.concat(segment), []);
            return {
              ...f,
              geometry: {
                type: "LineString",
                coordinates: flatCoords,
              },
            }
          })
        const passData = ({
          type: "FeatureCollection",
          features: filtered
        });

        const ev = await fetchActiveEvents(passData, "/events/route");
        // console.log("route ev", ev);
        if (!cancelled) {
          setOnlyActive(ev);
          setOnlyJam(ev.filter((e) => e.category?.id === 10));
        }
      } catch {
        if (!cancelled) {
          setOnlyActive([]);
          setOnlyJam([]);
        }
      } finally {
        if (!cancelled) {
          setLoadingEvents(false);
          hasLoadedEvents.current = true;
        }
      }
    };
    load();
    const iv = setInterval(load, 60_000);
    return () => {
      cancelled = true;
      clearInterval(iv);
    };
  }, [networkSource]);


  // 4️⃣ Memos (always run before returns)
  const routeMetrics = useMemo(
    () => selectedRoute?.metrics || [],
    [selectedRoute]
  );

  // 6️⃣ Memoize the stats arrays
  const dataOfEvents = useMemo(() => [{
    name: t("trafficMonitoring.monitoring.metrics.numberOfEvents.name"),
    values: [{ label: t("trafficMonitoring.monitoring.metrics.labels.total"), value: onlyActive.length, status: "neutral" }],
    description: t("trafficMonitoring.monitoring.metrics.numberOfEvents.descriptionRoute"),
  }], [onlyActive.length, t]);

  const dataOfJams = useMemo(() => [{
    name: t("trafficMonitoring.monitoring.metrics.numberOfJams.name"),
    values: [{ label: t("trafficMonitoring.monitoring.metrics.labels.total"), value: onlyJam.length, status: "neutral" }],
    description: t("trafficMonitoring.monitoring.metrics.numberOfJams.descriptionRoute"),
  }], [onlyJam.length, t]);

  const allMetrics = useMemo(
    () => [...routeMetrics, ...dataOfEvents, ...dataOfJams],
    [routeMetrics, dataOfEvents, dataOfJams]
  );

  // 7️⃣ First block: wait for routes + network
  if ((loadingRoutes || loadingNetwork) && !hasLoadedNetwork.current) {
    return (
      <Grid container justifyContent="center" alignItems="center" sx={{ height: "100%" }}>
        <CircularProgress />
      </Grid>
    );
  }
  hasLoadedNetwork.current = true;

  // 8️⃣ Second block: wait for first events load
  if (loadingEvents && !hasLoadedEvents.current) {
    return (
      <Grid container justifyContent="center" alignItems="center" sx={{ height: "100%" }}>
        <CircularProgress />
      </Grid>
    );
  }

 
return (
    <Grid
      container
      spacing={2}
      sx={{
        height: "100%", 
         minHeight: "80vh"
      }}
    >
      {/* Sidebar stats */}
      <Grid item xs={12} md={3}>
        <MDBox
          sx={{
            height: { xs: "auto", md: "100%" },
            display: "flex",
            flexDirection: "column",
          }}
        >
          <MDBox
            sx={{
              flexGrow: 1,
              overflowY: "auto",
              display: "flex",
              flexDirection: "column",
              gap: 2,
              py: 1,
            }}
          >
            <MemoTrafficList data={allMetrics} />
          </MDBox>
        </MDBox>
      </Grid>

      {/* Map + selector */}
      <Grid item xs={12} md={9}>
        <MDBox
          sx={{
             height: { xs: "420px", md: "100%" },
            display: "flex",
            flexDirection: "column",
          }}
        >
          <TrafficBar
            data={routes}
            selectedData={selectedRoute}
            setSelectedData={setSelectedRoute}
          />

          <MDBox
            sx={{
            flexGrow: 1,
               minHeight: { xs: "320px", md: 0 },
              position: "relative",
              overflow: "hidden",
            }}
          >
            <MemoTrafficMap
              onlyActive={onlyActive}
              networkSource={networkSource}
              areaRectSource={[]}
            />
          </MDBox>
        </MDBox>
      </Grid>
    </Grid>
  );

}
